import { useContext, useCallback } from 'react';
import _map from 'lodash/map';
import _get from 'lodash/get';
import _filter from 'lodash/filter';
import _find from 'lodash/find';

import { AjaxContext, AppContext, UserContext } from 'commons-ui';
import { KycCrmDashboardRequest } from 'commons/models/kyc.model';

import { UserDetailsContext } from 'context/userDetails';
import { ACTION, PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';

type UpdateKYCStatusPropsT = {
	message: string;
	status: string;
	kycId: any;
};

const useService = () => {
	const {
		app: { appName, isGold }
	} = useContext(AppContext);
	const { postData, putData } = useContext(AjaxContext);
	const { user } = useContext(UserContext);
	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);
	const { permissionsConfig } = useContext(UserContext);

	const updateKYCStatus = useCallback(async (props: UpdateKYCStatusPropsT) => {
		const { message, status, kycId } = props;
		const updates = [
			{
				message,
				kycId,
				status
			}
		];
		const permission = _find(permissionsConfig, { actionName: ACTION.updateKYCStatus });
		const result = await postData({
			endpoint: `/dashboard/updateKYCStatus/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-updateKYCStatus`,
			queryParams: {
				appName
			},
			body: {
				uid,
				updates,
				agentName: user?.userName,
				agentID: user?.email
			},
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		return result;
	}, []);

	const getKycDocsOptions = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getKycDocs });
		const result = await postData({
			endpoint: `/dashboard/getKycDocs/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-getKycDocs`,
			queryParams: {
				appName
			},
			body: {
				uid,
				isGold
			},
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});
		const kycDocs = Object.entries(_get(result, ['data', 'data', 'kycDocsData'], {}));
		return _map(
			_filter(kycDocs, (kycDoc) => kycDoc[0] !== 'AADHAR'),
			([key, value]) => ({
				id: value,
				label: key
			})
		);
	}, []);

	const submitKyc = useCallback(async (kycData: KycCrmDashboardRequest) => {
		const permission = _find(permissionsConfig, { actionName: ACTION.submitKyc });
		const result = await postData({
			endpoint: `/dashboard/submitKyc/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-submitKyc`,
			queryParams: {
				appName
			},
			body: {
				...kycData,
				uid
			},
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		return result;
	}, []);

	const getKycSignedUrl = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getKycSignedUrl });
		const result = await postData({
			endpoint: `/dashboard/getKycSignedUrl/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-getKycSignedUrl`,
			queryParams: {
				appName
			},
			body: {
				uid
			},
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});
		return result;
	}, []);

	const putKycDocToSignedUrl = useCallback(async (signedUrl: string, kycFile: File) => {
		const result = await putData({
			endpoint: '',
			baseUrlFromParams: signedUrl,
			body: kycFile
		});
		return result;
	}, []);
	return {
		updateKYCStatus,
		submitKyc,
		getKycDocsOptions,
		getKycSignedUrl,
		putKycDocToSignedUrl
	};
};

export default useService;
